/** @format */

import {MouseEvent, PointerEvent, useCallback, useEffect, useRef} from 'react';

import {COLUMN_ID_ATTRIBUTE, MIN_COLUMN_WIDTH} from '../../constants';
import {StyledColumnResizer} from './styles';

interface Props {
  // Id листовой колонки; по нему ручка находит свой <col> внутри таблицы.
  columnId: string;
  // Текущая ширина колонки (`header.getSize()`), от неё считается смещение драга.
  width: number;
  onResize: (columnId: string, width: number) => void;
  onReset: (columnId: string) => void;
}

interface DragState {
  startX: number;
  startWidth: number;
  currentWidth: number;
  colElement: HTMLElement | null;
  // Ручка, начавшая драг — нужна в finishDrag/cleanup, куда handle не долетает через event
  // (Escape слушается на document, cleanup при unmount вызывается вообще без события).
  handleElement: HTMLElement;
  // Слушатели window/document для ИМЕННО этого драга — храним ссылки, чтобы снять те же функции,
  // что были переданы в addEventListener (removeEventListener требует ту же identity).
  onWindowPointerUp: () => void;
  onWindowPointerCancel: () => void;
  onDocumentKeyDown: (event: KeyboardEvent) => void;
}

const ColumnResizer = ({columnId, width, onResize, onReset}: Props) => {
  // Драг живёт в ref: ни одно движение мыши не вызывает ре-рендер таблицы.
  const dragRef = useRef<DragState | null>(null);

  // Единая точка выхода из драга: снимает window-слушатели ИМЕННО этого драга, чистит
  // dragRef и стили body. Вызывается и при коммите, и при откате (см. finishDrag).
  const stopDrag = useCallback(() => {
    const drag = dragRef.current;
    if (drag) {
      window.removeEventListener('pointerup', drag.onWindowPointerUp);
      window.removeEventListener('pointercancel', drag.onWindowPointerCancel);
      document.removeEventListener('keydown', drag.onDocumentKeyDown);
    }
    dragRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Общий путь завершения драга. commit=true коммитит текущую ширину через onResize (если она
  // реально изменилась) — используется на обычном pointerup (на ручке или на window) и на
  // страховке в pointermove (buttons===0, см. handlePointerMove). commit=false откатывает <col>
  // к стартовой ширине без вызова onResize — используется для Escape, pointercancel и
  // unmount-посреди-драга: иначе <col> остаётся визуально resized, а колбэк наверх так и не
  // улетает (DOM и React-стейт расходятся). В обоих случаях снимает data-dragging с ручки —
  // без этого 1px-линия подсветки осталась бы навсегда.
  const finishDrag = useCallback(
    (commit: boolean) => {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }

      delete drag.handleElement.dataset.dragging;

      if (commit) {
        const finalWidth = drag.currentWidth;
        stopDrag();
        // Коммитим только если ширина реально изменилась: клик без движения (в т.ч. каждый из
        // двух pointerdown/pointerup нативного dblclick) не должен коммитить no-op resize.
        if (finalWidth !== drag.startWidth) {
          onResize(columnId, finalWidth);
        }
      } else {
        if (drag.colElement) {
          drag.colElement.style.width = `${drag.startWidth}px`;
        }
        stopDrag();
      }
    },
    [columnId, onResize, stopDrag],
  );

  const cancelDrag = useCallback(() => finishDrag(false), [finishDrag]);

  useEffect(
    () => () => {
      // Ручку могло размонтировать посреди драга: cancelDrag (а не просто stopDrag) откатывает
      // <col>, который переживает размонтирование резайзера — иначе React не сбросит
      // style.width, и колонка останется визуально "растянутой" навсегда. Заодно снимает
      // слушатели этого драга (см. stopDrag).
      if (dragRef.current) {
        cancelDrag();
      }
    },
    [cancelDrag],
  );

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    // Самая первая проверка, до любых сайд-эффектов: драг заводит только основная кнопка.
    // Правый/средний клик иначе начинал бы настоящий драг, чей pointerup съедает нативное
    // контекстное меню, dragRef остаётся заполненным навсегда, и колонка едет от обычного
    // ховера (pointermove без нажатых кнопок), а страницу нельзя выделить (user-select: none).
    if (event.button !== 0) {
      return;
    }

    // Иначе драг попутно переключает сортировку в родительской ячейке.
    event.stopPropagation();
    event.preventDefault();

    // Второй pointerdown может прийти раньше pointerup/pointercancel первого драга (multi-touch/
    // перо — второй указатель на той же 8px-ручке тоже даёт pointerdown с button===0). Без этой
    // подчистки: (1) window-слушатели первого драга никогда не снимутся — stopDrag снимает только
    // те, на которые ссылается ТЕКУЩИЙ dragRef.current, то есть уже второго; (2) если осиротевший
    // pointerup/pointercancel первого всё же долетит позже, он вызовет finishDrag, который
    // прочитает dragRef.current НА ТОТ МОМЕНТ — потенциально уже второй, ещё идущий драг — и
    // преждевременно закоммитит/откатит чужой активный драг. Чистим ДО создания нового dragRef:
    // finishDrag(false) откатывает именно ПЕРВЫЙ (пока ещё текущий на этот момент) драг и не
    // может задеть второй, которого ещё не существует. commit=false (а не stopDrag()), потому что
    // нужно ещё и откатить <col> первого драга к его стартовой ширине и снять с его ручки
    // data-dragging — иначе колонка/подсветка остались бы "застрявшими" в промежуточном состоянии
    // первого, никем больше не тронутого драга.
    if (dragRef.current) {
      finishDrag(false);
    }

    const handle = event.currentTarget;
    // Явная runtime-проверка вместо `?.`: TS типизирует setPointerCapture как обязательный
    // метод, но в средах без Pointer Events (см. brief) он реально может отсутствовать —
    // непроверенный вызов бросил бы TypeError и оборвал остаток хендлера (драг не начнётся).
    if (typeof handle.setPointerCapture === 'function') {
      handle.setPointerCapture(event.pointerId);
    }

    // Основной фикс: pointerup/pointercancel слушаем не только на самой ручке, а и на window.
    // Если пользователь отпускает кнопку за пределами ручки (док, второй монитор), браузер не
    // доставляет событие ручке — только window его видит. Без этого dragRef оставался бы
    // заполненным навсегда, ширина не коммитилась, а body — с user-select: none. Снимаются в
    // stopDrag (единая точка выхода, дергается и из commit-, и из rollback-пути finishDrag).
    const onWindowPointerUp = () => finishDrag(true);
    const onWindowPointerCancel = () => finishDrag(false);
    window.addEventListener('pointerup', onWindowPointerUp);
    window.addEventListener('pointercancel', onWindowPointerCancel);

    // Escape нужен только пока идёт драг, поэтому слушатель живёт ровно столько же: иначе каждая
    // ручка держала бы document-слушатель весь свой mount, а на широкой таблице это N глобальных
    // слушателей, дёргающихся на каждое нажатие клавиши в приложении. Снимается в stopDrag.
    const onDocumentKeyDown = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.key === 'Escape') {
        finishDrag(false);
      }
    };
    document.addEventListener('keydown', onDocumentKeyDown);

    // Стартуем от ЗАДАННОЙ ширины (`header.getSize()`), а не от измеренной: таблица не
    // растягивается до контейнера (`width: max-content` без `min-width`), поэтому заданная
    // ширина и есть отрендеренная. Это же число React держит в vDOM у <col>, поэтому
    // cancelDrag откатывает ровно к нему, и `columnSizing` не расползается на каждом драге
    // (измеренная ± delta накапливала бы погрешность).
    dragRef.current = {
      startX: event.clientX,
      startWidth: width,
      currentWidth: width,
      colElement:
        handle
          .closest('table')
          ?.querySelector<HTMLElement>(`col[${COLUMN_ID_ATTRIBUTE}="${columnId}"]`) ?? null,
      handleElement: handle,
      onWindowPointerUp,
      onWindowPointerCancel,
      onDocumentKeyDown,
    };
    handle.dataset.dragging = 'true';

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }

    if (event.buttons === 0) {
      // Страховка: кнопку уже отпустили, но ни ручка, ни window не получили ни pointerup, ни
      // pointercancel (событие потеряно). Коммитим уже накопленную ширину — пользователь видел
      // именно её на экране — а не откатываем, иначе визуальный результат разойдётся с
      // сохранённым.
      finishDrag(true);
      return;
    }

    const nextWidth = Math.max(MIN_COLUMN_WIDTH, drag.startWidth + (event.clientX - drag.startX));
    drag.currentWidth = nextWidth;
    if (drag.colElement) {
      drag.colElement.style.width = `${nextWidth}px`;
    }
  };

  const handlePointerUp = () => {
    finishDrag(true);
  };

  const handleDoubleClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onReset(columnId);
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return (
    <StyledColumnResizer
      data-testid={`column-resizer-${columnId}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={cancelDrag}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    />
  );
};

export {ColumnResizer};
